import { useEffect, useRef, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import Modal from './Modal';
import { TH, TD } from './Table';
import { toaster } from './toaster';
import { runBulkDelete, type BulkDeleteResult } from '@/lib/bulkDelete';
import type { RowSelection } from '@/lib/useRowSelection';

const checkboxClass = 'h-4 w-4 cursor-pointer rounded border-gray-300 dark:border-gray-700';

export const SelectAllHeader = <T,>({ selection }: { selection: RowSelection<T> }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selection.someSelected;
  }, [selection.someSelected]);
  return (
    <TH className="w-10 pr-0">
      <input
        ref={ref}
        type="checkbox"
        aria-label="Tout sélectionner"
        className={checkboxClass}
        checked={selection.allSelected}
        onChange={selection.toggleAll}
      />
    </TH>
  );
};

export const SelectRowCell = <T,>({ selection, row }: { selection: RowSelection<T>; row: T }) => (
  <TD className="w-10 pr-0">
    <input
      type="checkbox"
      aria-label="Sélectionner"
      className={checkboxClass}
      checked={selection.isSelected(row)}
      onChange={() => selection.toggle(row)}
    />
  </TD>
);

interface BulkDeleteBarProps<T> {
  selection: RowSelection<T>;
  labelOf: (row: T) => string;
  remove: (row: T) => Promise<void>;
  onDone: () => void;
}

export const BulkDeleteBar = <T,>({ selection, labelOf, remove, onDone }: BulkDeleteBarProps<T>) => {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BulkDeleteResult<T> | null>(null);
  const rows = selection.selectedRows;

  const close = () => {
    setOpen(false);
    setResult(null);
  };

  const confirm = async () => {
    setRunning(true);
    const outcome = await runBulkDelete(rows, remove);
    setRunning(false);
    selection.clear();
    onDone();
    if (outcome.failed.length === 0) {
      toaster.create({ title: `${outcome.deleted.length} élément(s) supprimé(s)`, type: 'success' });
      close();
    } else {
      setResult(outcome);
    }
  };

  if (rows.length === 0 && !open) return null;

  return (
    <>
      {rows.length > 0 && (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-brand-50 px-4 py-2 text-sm dark:border-gray-800 dark:bg-brand-950/40">
          <span className="font-medium text-gray-700 dark:text-gray-200">{rows.length} sélectionné(s)</span>
          <Button variant="ghost" size="sm" onClick={selection.clear}>
            Désélectionner
          </Button>
          <Button variant="danger" size="sm" className="ml-auto inline-flex items-center gap-1.5" onClick={() => setOpen(true)}>
            <TrashIcon className="h-4 w-4" />
            Supprimer la sélection
          </Button>
        </div>
      )}

      <Modal
        open={open}
        onClose={running ? () => undefined : close}
        title={result ? 'Suppression partielle' : `Supprimer ${rows.length} élément(s) ?`}
        footer={
          result ? (
            <Button onClick={close}>Fermer</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close} disabled={running}>
                Annuler
              </Button>
              <Button variant="danger" onClick={confirm} disabled={running}>
                {running ? 'Suppression…' : 'Supprimer'}
              </Button>
            </>
          )
        }
      >
        {result ? (
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <p>
              {result.deleted.length} élément(s) supprimé(s). {result.failed.length} non supprimé(s) :
            </p>
            <ul className="ml-4 max-h-64 list-disc space-y-1 overflow-y-auto">
              {result.failed.map(({ row, reason }, i) => (
                <li key={i}>
                  <span className="font-semibold">« {labelOf(row)} »</span> — {reason}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <ul className="ml-4 max-h-64 list-disc space-y-1 overflow-y-auto">
              {rows.map((row, i) => (
                <li key={i}>{labelOf(row)}</li>
              ))}
            </ul>
            <p>Les éléments dont la suppression est bloquée seront ignorés. Cette action est irréversible.</p>
          </div>
        )}
      </Modal>
    </>
  );
};
